import React, { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { addDoc, updateDoc, doc, collection, getDocs } from "firebase/firestore";

const categories = ["Poney", "Cheval", "Âne"];

// Couleurs par défaut si Firebase n'est pas disponible
const DEFAULT_TAG_COLORS = [
  { id: 1, nom: 'Bleu', codeHex: '#2563eb', ordre: 1 },
  { id: 2, nom: 'Rouge', codeHex: '#ef4444', ordre: 2 },
  { id: 3, nom: 'Vert', codeHex: '#22c55e', ordre: 3 },
  { id: 4, nom: 'Jaune', codeHex: '#eab308', ordre: 4 },
  { id: 5, nom: 'Orange', codeHex: '#f97316', ordre: 5 },
  { id: 6, nom: 'Violet', codeHex: '#8b5cf6', ordre: 6 },
  { id: 7, nom: 'Rose', codeHex: '#ec4899', ordre: 7 },
  { id: 8, nom: 'Turquoise', codeHex: '#06b6d4', ordre: 8 },
  { id: 9, nom: 'Gris', codeHex: '#6b7280', ordre: 9 },
  { id: 10, nom: 'Noir', codeHex: '#111827', ordre: 10 },
  { id: 11, nom: 'Marron', codeHex: '#92400e', ordre: 11 },
  { id: 12, nom: 'Blanc', codeHex: '#f3f4f6', ordre: 12 }
];

export default function AnimalFormModal({ animal, onClose }) {
  const [zones, setZones] = useState([]);
  const [tagColors, setTagColors] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [couleurs, setCouleurs] = useState([]);

  // Charger les zones depuis Firestore
  useEffect(() => {
    async function fetchZones() {
      const snap = await getDocs(collection(db, "lieux"));
      setZones(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }
    fetchZones();
  }, []);

  // Charger les couleurs de tags depuis Firebase
  useEffect(() => {
    async function fetchTagColors() {
      try {
        const tagColorsSnap = await getDocs(collection(db, "tagCouleursAnimaux"));
        let colorsData = [];
        
        tagColorsSnap.forEach(doc => {
          const data = doc.data();
          if (data.id && data.codeHex) {
            colorsData.push({ 
              id: data.id,
              nom: data.nom || '',
              codeHex: data.codeHex,
              ordre: data.ordre || data.id
            });
          }
        });

        // Trier par ordre
        colorsData.sort((a, b) => a.ordre - b.ordre);

        // Si pas de données Firebase, utiliser les couleurs par défaut
        if (colorsData.length === 0) {
          console.log('Aucune couleur trouvée dans tagCouleursAnimaux, utilisation des couleurs par défaut');
          colorsData = DEFAULT_TAG_COLORS;
        }

        setTagColors(colorsData);
      } catch (error) {
        console.error('Erreur lors du chargement des couleurs depuis tagCouleursAnimaux:', error);
        setTagColors(DEFAULT_TAG_COLORS);
      }
    }
    fetchTagColors();
  }, []);

  // Charger les propriétaires depuis Firestore
  useEffect(() => {
    async function fetchProps() {
      const snap = await getDocs(collection(db, "proprietaires"));
      setProprietaires(snap.docs.map(doc => ({
        id: doc.id,
        nom: doc.data().nom,
        prenom: doc.data().prenom
      })));
    }
    fetchProps();
  }, []);

  // Charger les couleurs de chevaux
  useEffect(() => {
    fetch('/horsecouleurs.txt')
      .then((r) => r.text())
      .then((text) => {
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        setCouleurs(lines);
      })
      .catch(() => {
        // Couleurs par défaut si le fichier n'existe pas
        setCouleurs(['Alezan', 'Bai', 'Noir', 'Gris', 'Palomino', 'Pie', 'Rouan']);
      });
  }, []);

  // État du formulaire
  const [form, setForm] = useState({
    type: "equide",
    nom: "",
    proprietaire: "",
    sire: "",
    dateNaissance: "",
    sexe: "",
    categorie: "",
    tag: "", // Sera un ID numérique en string
    transpondeur: "",
    zoneId: "",
    localisation: "",
    dateDeces: "",
    NotesGenerales: "",
    NotesSantee: "",
    SurveillanceEtat: "",
    ...animal,
  });

  // Obtenir la couleur du tag sélectionné
  const getSelectedTagColor = () => {
    if (!form.tag) return null;
    const numericId = parseInt(form.tag);
    return tagColors.find(tc => tc.id === numericId);
  };

  const selectedTagColor = getSelectedTagColor();
  const isEquide = form.type === "equide";

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function handleTagSelect(tagId) {
    setForm(prev => ({ ...prev, tag: tagId.toString() }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.id) {
      await updateDoc(doc(db, "animaux", form.id), form);
    } else {
      await addDoc(collection(db, "animaux"), form);
    }
    onClose();
  }

  return (
    <div className="fixed inset-0 z-30 bg-black bg-opacity-30 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-lg max-h-[80vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="mb-2 flex space-x-4">
            <label>
              <input
                type="radio"
                name="type"
                value="equide"
                checked={form.type === "equide"}
                onChange={handleChange}
              /> Équidé
            </label>
            <label>
              <input
                type="radio"
                name="type"
                value="animal"
                checked={form.type === "animal"}
                onChange={handleChange}
              /> Autre animal
            </label>
          </div>

          <div className="mb-2">
            <label className="block">Nom</label>
            <input
              className="border w-full px-2 py-1"
              name="nom"
              value={form.nom}
              onChange={handleChange}
              required
            />
          </div>

          {/* PROPRIETAIRE */}
          <div className="mb-2">
            <label className="block">Propriétaire</label>
            <select
              className="border w-full px-2 py-1"
              name="proprietaire"
              value={form.proprietaire}
              onChange={handleChange}
              required
            >
              <option value="">Choisir…</option>
              {proprietaires.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nom} {p.prenom}
                </option>
              ))}
            </select>
          </div>

          {/* TAG COULEUR AVEC PASTILLES */}
          <div className="mb-4">
            <label className="block mb-2 font-medium">Tag couleur (groupe/état juridique)</label>
            <div className="border rounded-lg p-3 bg-gray-50/50">
              {/* Sélection actuelle */}
              {selectedTagColor ? (
                <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm"
                      style={{ backgroundColor: selectedTagColor.codeHex }}
                    />
                    <span className="font-medium text-gray-900">{selectedTagColor.nom}</span>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">ID: {selectedTagColor.id}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTagSelect('')}
                    className="text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200 text-gray-500 text-center">
                  Aucun tag sélectionné
                </div>
              )}

              {/* Grille de sélection des couleurs */}
              <div className="grid grid-cols-6 gap-2">
                {tagColors.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagSelect(tag.id)}
                    className={`relative w-10 h-10 rounded-full border-2 hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md ${
                      selectedTagColor?.id === tag.id 
                        ? 'border-gray-700 ring-2 ring-gray-400 ring-offset-1' 
                        : 'border-gray-300 hover:border-gray-500'
                    }`}
                    style={{ backgroundColor: tag.codeHex }}
                    title={`${tag.nom} (ID: ${tag.id})`}
                  >
                    {selectedTagColor?.id === tag.id && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 bg-gray-800 rounded-full shadow-sm border border-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {tagColors.length === 0 && (
                <div className="text-center text-gray-500 py-6 bg-white rounded-lg border border-gray-200">
                  <div className="text-gray-400 text-lg mb-2">⚙️</div>
                  <p className="font-medium">Aucune couleur de tag configurée</p>
                  <p className="text-sm mt-1">Configurez les couleurs dans les Paramètres</p>
                </div>
              )}
            </div>
          </div>

          {/* ZONE DE LOCALISATION */}
          <div className="mb-2">
            <label className="block">Zone de localisation</label>
            <select
              className="border w-full px-2 py-1"
              name="zoneId"
              value={form.zoneId || ""}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionner une zone…</option>
              {zones.map(z => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>

          {isEquide && (
            <>
              <div className="mb-2">
                <label className="block">N° SIRE</label>
                <input
                  className="border w-full px-2 py-1"
                  name="sire"
                  value={form.sire}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-2">
                <label className="block">Date de naissance</label>
                <input
                  className="border w-full px-2 py-1"
                  name="dateNaissance"
                  value={form.dateNaissance}
                  type="date"
                  onChange={handleChange}
                />
              </div>
              <div className="mb-2">
                <label className="block">Sexe</label>
                <select
                  className="border w-full px-2 py-1"
                  name="sexe"
                  value={form.sexe}
                  onChange={handleChange}
                >
                  <option value="">Choisir…</option>
                  <option value="M">M</option>
                  <option value="F">F</option>
                  <option value="Hongre">Hongre</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block mb-1 font-medium">Couleur</label>
                <select
                  className="w-full border p-2 rounded"
                  value={form.couleur || ''}
                  name="couleur"
                  onChange={handleChange}
                  required
                >
                  <option value="">Sélectionner la couleur</option>
                  {couleurs.map(couleur => (
                    <option key={couleur} value={couleur}>{couleur}</option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="block">Catégorie</label>
                <select
                  className="border w-full px-2 py-1"
                  name="categorie"
                  value={form.categorie}
                  onChange={handleChange}
                >
                  <option value="">Choisir…</option>
                  {categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label className="block">N° Transpondeur</label>
                <input
                  className="border w-full px-2 py-1"
                  name="transpondeur"
                  value={form.transpondeur}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-2">
                <label className="block">Localisation interne</label>
                <input
                  className="border w-full px-2 py-1"
                  name="localisation"
                  value={form.localisation}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-2">
                <label className="block">Date de décès</label>
                <input
                  className="border w-full px-2 py-1"
                  name="dateDeces"
                  value={form.dateDeces}
                  type="date"
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          
          <div className="mb-2">
            <label className="block">Notes générales</label>
            <textarea
              className="border w-full px-2 py-1"
              name="NotesGenerales"
              value={form.NotesGenerales}
              onChange={handleChange}
              rows={2}
            />
          </div>
          <div className="mb-2">
            <label className="block">Notes de santé</label>
            <textarea
              className="border w-full px-2 py-1"
              name="NotesSantee"
              value={form.NotesSantee}
              onChange={handleChange}
              rows={2}
            />
          </div>
          <div className="mb-2">
            <label className="block">État de surveillance</label>
            <select
              className="border w-full px-2 py-1"
              name="SurveillanceEtat"
              value={form.SurveillanceEtat}
              onChange={handleChange}
              required
            >
              <option value="">Choisir…</option>
              <option value="vert">Surveillance normale (Vert)</option>
              <option value="jaune">Attention légère (Jaune)</option>
              <option value="orange">Vigilance accrue (Orange)</option>
              <option value="rouge">Surveillance critique (Rouge)</option>
            </select>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              className="px-6 py-2 border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors font-medium"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-medium shadow-sm"
            >
              {form.id ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}